import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { ScrollToPageOptions } from './pageJump';
import { scrollToPage } from './pageJump';
import { getPdfPageView } from './pdfPageView';
import type { PdfFitMode, PdfZoomDirection } from './pdfViewerScale';
import { zoomPdfViewer } from './pdfViewerScale';
import type { PdfDocumentSource, PdfPageLayer } from './types';
import { usePdfDocumentActions } from './usePdfDocumentActions';
import { usePdfPageLayers } from './usePdfPageLayers';
import { usePdfPageNavigation } from './usePdfPageNavigation';
import { usePdfSearch } from './usePdfSearch';
import { usePdfViewerGestures } from './usePdfViewerGestures';
import { usePdfViewerResize } from './usePdfViewerResize';
import {
  type PdfViewerRuntimeCallbacks,
  usePdfViewerRuntime,
} from './usePdfViewerRuntime';
import { useViewerHotkeys } from './useViewerHotkeys';

import sharedStyles from './documentPreviewShared.module.scss';

type UsePdfDocumentPreviewOptions = {
  source: PdfDocumentSource | null;
  initialPage: number;
  markedPages: readonly number[];
  pageLayers: readonly PdfPageLayer[];
  showThumbnails: boolean;
  filename: string;
  onLoad?: (pageCount: number) => void;
  onError?: (error: Error) => void;
  onPageChange?: (pageNumber: number) => void;
};

const noOp = () => undefined;

export function usePdfDocumentPreview({
  source,
  initialPage,
  markedPages,
  pageLayers,
  showThumbnails,
  filename,
  onLoad,
  onError,
  onPageChange,
}: UsePdfDocumentPreviewOptions) {
  const [isPagesOpen, setIsPagesOpen] = useState(showThumbnails);
  const [isHandTool, setIsHandTool] = useState(false);
  const [isPanning, setIsPanning] = useState(false);
  const [isHorizontal, setIsHorizontal] = useState(false);
  const activePageJumpRef = useRef<(() => void) | null>(null);
  const pageJumpIdRef = useRef(0);

  const callbacksRef = useRef<PdfViewerRuntimeCallbacks>({
    onDocumentReset: noOp,
    onFindMatches: noOp,
    onPageChanging: noOp,
    onPageRendered: noOp,
    onPagesInit: noOp,
    cleanupPageLayers: noOp,
  });

  const cancelPageJump = useCallback(() => {
    pageJumpIdRef.current += 1;
    const cancel = activePageJumpRef.current;
    activePageJumpRef.current = null;
    cancel?.();
  }, []);

  const startPageJump = useCallback(
    (options: ScrollToPageOptions, onCancel: () => void) => {
      cancelPageJump();
      const jumpId = ++pageJumpIdRef.current;
      let completed = false;
      const cancel = scrollToPage({
        ...options,
        onDone: (animated) => {
          completed = true;
          if (pageJumpIdRef.current === jumpId) {
            activePageJumpRef.current = null;
          }
          options.onDone(animated);
        },
      });
      if (!completed && pageJumpIdRef.current === jumpId) {
        activePageJumpRef.current = () => {
          cancel();
          onCancel();
        };
      }
    },
    [cancelPageJump]
  );

  const runtime = usePdfViewerRuntime({
    source,
    initialPage,
    callbacksRef,
    onLoad,
    onError,
  });

  const search = usePdfSearch({
    eventBusRef: runtime.eventBusRef,
  });

  const pageLayersRuntime = usePdfPageLayers({
    pageLayers,
    viewerRef: runtime.viewerRef,
  });

  const highlightPage = useCallback(
    (pageNumber: number) => {
      const pageElement = getPdfPageView(
        runtime.viewerRef.current,
        pageNumber
      )?.div;
      if (!pageElement) {
        return;
      }

      const flashClass = sharedStyles['page-flash'];
      pageElement.classList.add(flashClass);
      pageElement.addEventListener(
        'animationend',
        () => pageElement.classList.remove(flashClass),
        { once: true }
      );
    },
    [runtime.viewerRef]
  );

  const navigation = usePdfPageNavigation({
    initialPage,
    status: runtime.status,
    isHorizontal,
    containerRef: runtime.containerRef,
    viewerRef: runtime.viewerRef,
    cancelPageJump,
    startPageJump,
    onHighlightPage: highlightPage,
    onPageChange,
  });

  const actions = usePdfDocumentActions({ source, filename });
  const markedPageSet = useMemo(() => new Set(markedPages), [markedPages]);
  const toggleRuntimeScrollMode = runtime.toggleScrollMode;

  callbacksRef.current = {
    onDocumentReset: (pageNumber) => {
      navigation.resetNavigation(pageNumber);
      search.resetSearch();
    },
    onFindMatches: search.handleFindMatches,
    onPageChanging: navigation.handlePageChanging,
    onPageRendered: (pageNumber) => {
      pageLayersRuntime.handlePageRendered(pageNumber);
      navigation.handlePageRendered(pageNumber);
    },
    onPagesInit: navigation.handlePagesInit,
    cleanupPageLayers: pageLayersRuntime.cleanupLayers,
  };

  useEffect(() => {
    setIsPagesOpen(showThumbnails);
  }, [showThumbnails]);

  useEffect(() => cancelPageJump, [cancelPageJump]);

  usePdfViewerGestures({
    containerRef: runtime.containerRef,
    viewerRef: runtime.viewerRef,
    fitModeRef: runtime.fitModeRef,
    isHandTool,
    cancelPageJump,
    onPanChange: setIsPanning,
  });

  usePdfViewerResize({
    containerRef: runtime.containerRef,
    viewerRef: runtime.viewerRef,
    fitModeRef: runtime.fitModeRef,
  });

  const zoom = useCallback(
    (direction: PdfZoomDirection) => {
      const viewer = runtime.viewerRef.current;
      if (!viewer) {
        return;
      }
      runtime.fitModeRef.current = null;
      zoomPdfViewer(viewer, direction);
    },
    [runtime.fitModeRef, runtime.viewerRef]
  );

  const setFit = useCallback(
    (mode: Exclude<PdfFitMode, null>) => {
      const viewer = runtime.viewerRef.current;
      if (viewer) {
        runtime.fitModeRef.current = mode;
        viewer.currentScaleValue = mode;
      }
    },
    [runtime.fitModeRef, runtime.viewerRef]
  );

  const toggleScrollMode = useCallback(() => {
    const horizontal = toggleRuntimeScrollMode();
    if (horizontal !== undefined) {
      setIsHorizontal(horizontal);
    }
  }, [toggleRuntimeScrollMode]);

  const rotate = useCallback(() => {
    const viewer = runtime.viewerRef.current;
    if (viewer) {
      viewer.pagesRotation = (viewer.pagesRotation + 90) % 360;
    }
  }, [runtime.viewerRef]);

  useViewerHotkeys({
    closeSearch: search.closeSearch,
    isSearchOpen: search.isSearchOpen,
    openSearch: search.openSearch,
  });

  return {
    status: runtime.status,
    numPages: runtime.numPages,
    currentPage: navigation.currentPage,
    scalePct: runtime.scalePct,
    isPagesOpen,
    isSearchOpen: search.isSearchOpen,
    query: search.query,
    matches: search.matches,
    isHandTool,
    isPanning,
    isHorizontal,
    markedPageSet,
    pdf: runtime.pdf,
    containerRef: runtime.containerRef,
    viewerElementRef: runtime.viewerElementRef,
    setQuery: search.setQuery,
    togglePages: () => setIsPagesOpen((open) => !open),
    toggleHandTool: () => setIsHandTool((active) => !active),
    openSearch: search.openSearch,
    closeSearch: search.closeSearch,
    toggleSearch: search.toggleSearch,
    jumpToPage: navigation.jumpToPage,
    zoom,
    setFit,
    runFind: search.runFind,
    toggleScrollMode,
    rotate,
    download: actions.download,
    print: actions.print,
  };
}
